import * as Dfns from 'date-fns/fp';

// Shared between LinearOptimizations and StreamingOptimizations -- both pages edit
// constraint/optimization tables and debug constraint runs the same way, even though their
// backend lambdas, constraint schemas, and Run Views differ (see each page's own utils for
// domain-specific logic).

const PRETTY_DATE_FORMAT = 'yyyy-MM-dd hh:mma';

export const formatOptimizationDate = (value: string): string => {
  try {
    return Dfns.format(PRETTY_DATE_FORMAT)(Dfns.parseISO(value));
  } catch {
    return value;
  }
};

export const getConstraintNameError = (name: string): string | undefined => {
  if (!name) return 'Name your constraint to continue';
  if (name === '.' || name === '..') return 'Invalid name';

  const invalid = [...new Set(name.match(/[\\/=@?#&%$:]/g) ?? [])];
  return invalid.length > 0 ? `Invalid name contains: ${invalid.join(', ')}` : undefined;
};

export interface IdentifiedRow {
  id?: number | string;
  index?: number;
}

const rowKey = ({ id, index }: IdentifiedRow): number | string | undefined => id ?? index;

// Every element of a constraint table's array sections gets a positional `index` stamped on fetch
// and re-stamped on every save -- editor tables key rows by it (not by any backend id, since new
// rows don't have one yet), so it must stay unique/dense or applyTableChanges' modifiedByKey Map
// collapses distinct rows that share a stale (or missing) index onto each other.
export const withIndexes = <T extends IdentifiedRow>(rows: T[] = []): T[] =>
  rows.map((row, index) => ({ ...row, index }));

// Merges a ModalEditWidget's addedData/modifiedData/deletedData back into the original array by
// row identity (backend `id` when present, else the positional `index` stamped by withIndexes)
// rather than by any editable field -- keying on an editable field would drop the edit if that
// field itself was the one renamed.
export const applyTableChanges = <T extends IdentifiedRow>(
  rows: T[],
  added: T[],
  modified: T[],
  deleted: T[]
): T[] => {
  const deletedKeys = new Set(deleted.map(rowKey));
  const modifiedByKey = new Map(modified.map(row => [rowKey(row), row]));

  return [
    ...rows
      .filter(row => !deletedKeys.has(rowKey(row)))
      .map(row => modifiedByKey.get(rowKey(row)) ?? row),
    ...added
  ];
};

export interface RawConstraintDebuggerRow {
  Name: string;
  MinConstraint: string;
  MaxConstraint: string;
  FinalValue: string;
  FinalValueNonFractional: string;
  BindingMin: string;
  BindingMax: string;
}

export const ConstraintBindingStatus = {
  AT_MINIMUM: 'At Minimum',
  AT_MAXIMUM: 'At Maximum',
  NOT_BINDING: 'Not binding'
} as const;
export type ConstraintBindingStatusType =
  (typeof ConstraintBindingStatus)[keyof typeof ConstraintBindingStatus];

export interface ConstraintDebuggerRow {
  constraintType: string;
  Name: string;
  MinConstraint: number;
  MaxConstraint: number;
  FinalValue: number;
  FinalValueNonFractional: number | '-';
  isBinding: ConstraintBindingStatusType;
  NonFractionalSurplus: number | 'None';
  NonFractionalDeficit: number | 'None';
}

export const transformConstraintDebuggerRow = (
  row: RawConstraintDebuggerRow
): ConstraintDebuggerRow => {
  // "Type:Rest of name" -- constraints without a ':' predate this convention.
  const parsed = row.Name.split(':');
  const constraintType = parsed.length === 1 ? 'Unknown' : parsed[0];
  const name = parsed.length === 1 ? row.Name : parsed.slice(1).join('');

  let isBinding: ConstraintDebuggerRow['isBinding'] = ConstraintBindingStatus.NOT_BINDING;
  if (row.BindingMin === '1') {
    isBinding = ConstraintBindingStatus.AT_MINIMUM;
  } else if (row.BindingMax === '1') {
    isBinding = ConstraintBindingStatus.AT_MAXIMUM;
  }

  const finalValue = parseFloat(row.FinalValue);
  const finalValueNonFractional = row.FinalValueNonFractional
    ? parseInt(row.FinalValueNonFractional, 10)
    : ('-' as const);
  const minConstraint = parseInt(row.MinConstraint, 10);
  const maxConstraint = parseInt(row.MaxConstraint, 10);

  let nonFractionalSurplus: number | 'None' = 'None';
  let nonFractionalDeficit: number | 'None' = 'None';
  if (finalValueNonFractional !== '-' && !Number.isNaN(finalValue)) {
    if (finalValueNonFractional > maxConstraint) {
      nonFractionalSurplus = finalValueNonFractional - maxConstraint;
    } else if (finalValueNonFractional < minConstraint) {
      nonFractionalDeficit = minConstraint - finalValueNonFractional;
    }
  }

  return {
    constraintType,
    Name: name,
    MinConstraint: minConstraint,
    MaxConstraint: maxConstraint,
    FinalValue: finalValue,
    FinalValueNonFractional: finalValueNonFractional,
    isBinding,
    NonFractionalSurplus: nonFractionalSurplus,
    NonFractionalDeficit: nonFractionalDeficit
  };
};
